import React from 'react';

import { Divider, Skeleton, Stack, Title, createStyles } from '@mantine/core';
import { IconLock } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useGym from 'hooks/gym/useGym';
import useTotalRevenue from 'hooks/reports/revenue/useTotalRevenue';
import useUpcomingRevenue from 'hooks/reports/revenue/useUpcomingRevenue';
import { useNavigate } from 'react-router-dom';
import { formatDateForApi, normalizeTimezone } from 'utils/date-functions';

import { useDashboardStyles } from '../styles';

dayjs.extend(utc);
dayjs.extend(timezone);

const useStyles = createStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `
      "total upcoming"
      `,
    gap: theme.spacing.sm,
    padding: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'auto',
      gridTemplateAreas: `
      "total"
      "upcoming"
      `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto',
      gridTemplateAreas: `
      "total upcoming"
      `,
    },
    [theme.fn.smallerThan('xs')]: {
      gridTemplateColumns: 'auto',
      gridTemplateAreas: `
      "total"
      "upcoming"
      `,
    },
  },
  total: {
    gridArea: 'total',
  },
  upcoming: {
    gridArea: 'upcoming',
  },
}));

const AccountingCard = (): JSX.Element => {
  const { classes: dashboard } = useDashboardStyles();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { formatCurrency } = useLocalization();
  const { hasPerm, session } = useAuth();

  const isAccountingViewable = hasPerm('AccountingReportsView');

  const { data: gymData } = useGym(session?.gym?.gymId);
  const gymTimezone = normalizeTimezone(gymData?.data?.timezone || 'UTC');
  const gymDayjs = dayjs().tz(gymTimezone);

  // Create date ranges directly in gym timezone using dayjs
  const monthStartDayjs = gymDayjs.startOf('month');
  const monthEndDayjs = gymDayjs.endOf('month');
  const todayEndDayjs = gymDayjs.endOf('day');
  const tomorrowDayjs = gymDayjs.add(1, 'day').startOf('day');

  const {
    data: totalRevenue,
    isLoading: totalRevenueIsLoading,
    isError: totalRevenueIsError,
    isSuccess: totalRevenueIsSuccess,
  } = useTotalRevenue({
    paymentDateStart: formatDateForApi(monthStartDayjs) ?? null,
    paymentDateEnd: formatDateForApi(todayEndDayjs) ?? null,
    enabled: isAccountingViewable,
  });
  const {
    data: upcomingRevenue,
    isLoading: upcomingRevenueIsLoading,
    isError: upcomingRevenueIsError,
    isSuccess: upcomingRevenueIsSuccess,
  } = useUpcomingRevenue({
    dueDateStart: formatDateForApi(tomorrowDayjs) ?? null,
    dueDateEnd: formatDateForApi(monthEndDayjs) ?? null,
    enabled: isAccountingViewable,
  });

  const handleReportClick = (report: string): void => {
    if (isAccountingViewable) {
      navigate(`/reports/${report}`);
    }
  };

  const getTotalRevenue = (): JSX.Element => {
    if (!isAccountingViewable) {
      return <IconLock size='2rem' />;
    }
    if (totalRevenueIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (totalRevenueIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (totalRevenueIsSuccess && totalRevenue) {
      return <div className={dashboard.value}>{formatCurrency(totalRevenue.data.total / 100)}</div>;
    }
    return <></>;
  };

  const getUpcomingRevenue = (): JSX.Element => {
    if (!isAccountingViewable) {
      return <IconLock size='2rem' />;
    }
    if (upcomingRevenueIsLoading) {
      return <Skeleton height='2rem' width='75%' />;
    }
    if (upcomingRevenueIsError) {
      return <div className={dashboard.value}>Error</div>;
    }
    if (upcomingRevenueIsSuccess && upcomingRevenue) {
      return <div className={dashboard.value}>{formatCurrency(upcomingRevenue.data.total / 100)}</div>;
    }
    return <></>;
  };

  return (
    <div className={`${dashboard.accounting} ${dashboard.card}`}>
      <div className={dashboard.cardHeader}>
        <Title order={4}>Accounting</Title>
      </div>

      <Divider />

      <div className={classes.grid}>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.total} ${isAccountingViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('total-revenue')}
        >
          <div className={dashboard.label}>Total Revenue MTD</div>
          {getTotalRevenue()}
        </Stack>
        <Stack
          spacing={0}
          p='xs'
          className={`${classes.upcoming} ${isAccountingViewable ? dashboard.clickableCard : undefined}`}
          onClick={() => handleReportClick('upcoming-revenue')}
        >
          <div className={dashboard.label}>{`Upcoming Revenue ${dayjs(new Date()).format('MMM')}`}</div>
          {getUpcomingRevenue()}
        </Stack>
      </div>
    </div>
  );
};

export default AccountingCard;
